import { LoadingState } from '@/entities/LoadingState';
import { MutationTree, ActionTree, GetterTree, Module, ActionContext } from 'vuex';
import { RootState } from '../rootState';
import { CountryService } from '@/services/country-service';
import { Country } from '@/entities/Country';
import { State } from '@/entities/State';
import removeNamespace from '../removeNamespace';

const countryService = new CountryService();

const namespace = 'countryStateSelect';

export const CountryStateSelectModuleTypes = {
    getters: {
        countries: `${namespace}/countries`,
        countriesLoadingState: `${namespace}/countriesLoadingState`,
        selectedCountry: `${namespace}/selectedCountry`,
        statesOfSelectedCountry: `${namespace}/statesOfSelectedCountry`,
        statesLoadingState: `${namespace}/statesLoadingState`,
        selectedState: `${namespace}/selectedState`,
    },
    mutations: {
        registerSelect: `${namespace}/registerSelect`,
        loadCountries: `${namespace}/loadCountries`,
        loadCountriesSuccess: `${namespace}/loadCountriesSuccess`,
        loadCountriesFail: `${namespace}/loadCountriesFail`,
        selectCountry: `${namespace}/selectCountry`,
        selectCountryById: `${namespace}/selectCountryById`,
        loadStatesForSelectedCountry: `${namespace}/loadStatesForSelectedCountry`,
        loadStatesForSelectedCountrySuccess: `${namespace}/loadStatesForSelectedCountrySuccess`,
        loadStatesForSelectedCountryFail: `${namespace}/loadStatesForSelectedCountryFail`,
        selectState: `${namespace}/selectState`,
        selectStateById: `${namespace}/selectStateById`,
    },
    actions: {
        registerSelect: `${namespace}/registerSelect`,
        selectCountry: `${namespace}/selectCountry`,
        selectCountryAndStateById: `${namespace}/selectCountryAndStateById`,
        selectState: `${namespace}/selectState`,
    },
};

const types = removeNamespace(namespace, CountryStateSelectModuleTypes);

export interface CountryStateSelectState {
    selectedCountry: Country | null;
    selectedState: State | null;
    statesOfSelectedCountry: State[];
    statesLoadingState: LoadingState;
}

export interface CountryStates {
    countries: Country[];
    countriesLoadingState: LoadingState;
    countryStateSelectStates: { [key: string]: CountryStateSelectState };
}

const state: CountryStates = {
    countries: [],
    countriesLoadingState: LoadingState.Indeterminate,
    countryStateSelectStates: {},
};

const mutations: MutationTree<CountryStates> = {
    [types.mutations.registerSelect](currentState: CountryStates, id: string): void {
        if (currentState.countryStateSelectStates[id] == null) {
            currentState.countryStateSelectStates[id] = {
                selectedCountry: null,
                selectedState: null,
                statesOfSelectedCountry: [],
                statesLoadingState: LoadingState.Indeterminate,
            };
        }
    },
    [types.mutations.loadCountries](currentState: CountryStates): void {
        currentState.countriesLoadingState = LoadingState.Loading;
    },
    [types.mutations.loadCountriesSuccess](currentState: CountryStates, payload: Country[]): void {
        currentState.countries = payload;
        currentState.countriesLoadingState = LoadingState.Loaded;
    },
    [types.mutations.loadCountriesFail](currentState: CountryStates): void {
        currentState.countries = [];
        currentState.countriesLoadingState = LoadingState.Failed;
    },
    [types.mutations.selectCountry](currentState: CountryStates, payload: { id: string, country: Country }): void {
        const countryStateSelectState = currentState.countryStateSelectStates[payload.id];
        if (countryStateSelectState) {
            countryStateSelectState.selectedCountry = payload.country;
            // State has to be reset if a new country is selected
            countryStateSelectState.selectedState = null;
            // copy array and replace country-state-select-state on given id, otherwise changes are not recognized
            currentState.countryStateSelectStates = {
                ...currentState.countryStateSelectStates,
                [payload.id]: countryStateSelectState,
            };
        }
    },
    [types.mutations.selectCountryById](currentState: CountryStates, payload: { id: string, countryId: number }): void {
        const foundCountry = currentState.countries.find((country) => country.id === payload.countryId) || null;
        const countryStateSelectState = currentState.countryStateSelectStates[payload.id];
        if (foundCountry && countryStateSelectState) {
            countryStateSelectState.selectedCountry = foundCountry;
            // State has to be reset if a new country is selected
            countryStateSelectState.selectedState = null;
            // copy array and replace country-state-select-state on given id, otherwise changes are not recognized
            currentState.countryStateSelectStates = {
                ...currentState.countryStateSelectStates,
                [payload.id]: countryStateSelectState,
            };
        }
    },
    [types.mutations.loadStatesForSelectedCountry](currentState: CountryStates, payload: { id: string }): void {
        const countryStateSelectState = currentState.countryStateSelectStates[payload.id];
        if (countryStateSelectState) {
            countryStateSelectState.statesLoadingState = LoadingState.Loading;
        }
    },
    [types.mutations.loadStatesForSelectedCountrySuccess](currentState: CountryStates, payload: { id: string, states: State[] }): void {
        const countryStateSelectState = currentState.countryStateSelectStates[payload.id];
        if (countryStateSelectState) {
            countryStateSelectState.statesOfSelectedCountry = payload.states;
            countryStateSelectState.statesLoadingState = LoadingState.Loaded;
        }
    },
    [types.mutations.loadStatesForSelectedCountryFail](currentState: CountryStates, payload: { id: string }): void {
        const countryStateSelectState = currentState.countryStateSelectStates[payload.id];
        if (countryStateSelectState) {
            countryStateSelectState.statesOfSelectedCountry = [];
            countryStateSelectState.statesLoadingState = LoadingState.Failed;
        }
    },
    [types.mutations.selectState](currentState: CountryStates, payload: { id: string, state: State }): void {
        const countryStateSelectState = currentState.countryStateSelectStates[payload.id];
        if (countryStateSelectState) {
            countryStateSelectState.selectedState = payload.state;
            // copy array and replace country-state-select-state on given id, otherwise changes are not recognized
            currentState.countryStateSelectStates = {
                ...currentState.countryStateSelectStates,
                [payload.id]: countryStateSelectState,
            };
        }
    },
    [types.mutations.selectStateById](currentState: CountryStates, payload: { id: string, stateId: number }): void {
        const countryStateSelectState = currentState.countryStateSelectStates[payload.id];
        const foundState = countryStateSelectState.statesOfSelectedCountry.find((stateOfCountry) => stateOfCountry.id === payload.stateId);
        if (foundState && countryStateSelectState) {
            countryStateSelectState.selectedState = foundState;
            // copy array and replace country-state-select-state on given id, otherwise changes are not recognized
            currentState.countryStateSelectStates = {
                ...currentState.countryStateSelectStates,
                [payload.id]: countryStateSelectState,
            };
        }
    },
};

const actions: ActionTree<CountryStates, RootState> = {
    [types.actions.registerSelect](context: ActionContext<CountryStates, RootState>, payload: { id: number }): void {
        context.commit(types.mutations.registerSelect, payload.id);
        if (context.state.countries.length === 0 && context.state.countriesLoadingState !== LoadingState.Loading) {
            context.commit(types.mutations.loadCountries);
            countryService.getAllCountries()
                .then((countries) => context.commit(types.mutations.loadCountriesSuccess, countries))
                .catch((e) => context.commit(types.mutations.loadCountriesFail));
        }
    },
    [types.actions.selectCountry](context: ActionContext<CountryStates, RootState>, payload: { id: string, country: Country }): void {
        context.commit(types.mutations.selectCountry, payload);
        context.commit(types.mutations.loadStatesForSelectedCountry, { id: payload.id });
        countryService.getStatesOfCountry(payload.country.id)
            .then((states) => context.commit(types.mutations.loadStatesForSelectedCountrySuccess, { id: payload.id, states }))
            .catch((e) => context.commit(types.mutations.loadStatesForSelectedCountryFail, { id: payload.id }));
    },
    [types.actions.selectCountryAndStateById](
        context: ActionContext<CountryStates, RootState>,
        payload: { id: string, countryId: number, stateId: number }): void {
        context.commit(types.mutations.selectCountryById, { id: payload.id, countryId: payload.countryId });
        context.commit(types.mutations.loadStatesForSelectedCountry, { id: payload.id });
        countryService.getStatesOfCountry(payload.countryId)
            .then((states) => {
                context.commit(types.mutations.loadStatesForSelectedCountrySuccess, { id: payload.id, states });
                context.commit(types.mutations.selectStateById, { id: payload.id, stateId: payload.stateId });
            })
            .catch((e) => context.commit(types.mutations.loadStatesForSelectedCountryFail, { id: payload.id }));
    },
    [types.actions.selectState](context: ActionContext<CountryStates, RootState>, payload: { id: string, state: State }): void {
        context.commit(types.mutations.selectState, payload);
    },
};

const getters: GetterTree<CountryStates, RootState> = {
    [types.getters.countries](currentState: CountryStates): Country[] {
        return currentState.countries;
    },
    [types.getters.countriesLoadingState](currentState: CountryStates): LoadingState {
        return currentState.countriesLoadingState;
    },
    [types.getters.selectedCountry](currentState: CountryStates): (id: string) => Country | null {
        return (id: string) => currentState.countryStateSelectStates[id] && currentState.countryStateSelectStates[id].selectedCountry;
    },
    [types.getters.statesOfSelectedCountry](currentState: CountryStates): (id: string) => State[] | null {
        return (id: string) => currentState.countryStateSelectStates[id] && currentState.countryStateSelectStates[id].statesOfSelectedCountry;
    },
    [types.getters.statesLoadingState](currentState: CountryStates): (id: string) => LoadingState {
        return (id: string) => currentState.countryStateSelectStates[id] && currentState.countryStateSelectStates[id].statesLoadingState;
    },
    [types.getters.selectedState](currentState: CountryStates): (id: string) => State | null {
        return (id: string) => currentState.countryStateSelectStates[id] && currentState.countryStateSelectStates[id].selectedState;
    },
};

const module: Module<CountryStates, RootState> = {
    state,
    mutations,
    actions,
    getters,
    namespaced: true,
};

export default module;
